// Sentry initialization for the tenant OS frontend.
// No-op when REACT_APP_SENTRY_DSN is unset — safe to merge before the
// Sentry org exists.
//
// Usage:
//   import { initSentry, captureException } from '@/lib/sentry';
//   initSentry();   // call once at app boot
//   captureException(err, { module: 'sales' });

import * as Sentry from '@sentry/react';

let _initialized = false;

export function initSentry() {
  if (_initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;                         // dev / staging without DSN → no-op

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENV || 'production',
    release:     process.env.REACT_APP_RELEASE || undefined,
    tracesSampleRate:        Number(process.env.REACT_APP_SENTRY_TRACES_RATE || 0.1),
    replaysSessionSampleRate: 0,            // off by default — privacy
    replaysOnErrorSampleRate: 0.1,          // 10% of error sessions get replay
    sendDefaultPii: false,                  // GDPR — never auto-attach user info
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText:   true,
        blockAllMedia: true,
      }),
    ],
    // Drop noisy events that hit every browser
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event) {
      // Strip query strings — they can carry sensitive ids/tokens
      if (event.request?.url) {
        try { event.request.url = event.request.url.split('?')[0]; } catch {}
      }
      return event;
    },
  });
  _initialized = true;
}

export function captureException(error, context = {}) {
  if (!_initialized) return;
  Sentry.withScope((scope) => {
    Object.entries(context || {}).forEach(([k, v]) => scope.setTag(k, String(v)));
    Sentry.captureException(error);
  });
}

export function setUserContext(user) {
  if (!_initialized || !user) return;
  // Pass minimal identifying info — never email/phone/PII fields
  Sentry.setUser({
    id:   user.id || user._id,
    role: user.role,
  });
}

export function clearUserContext() {
  if (!_initialized) return;
  Sentry.setUser(null);
}
